import { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

export default function VendorsPage() {
  const [vendors, setVendors] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ criticality: '', risk_rating: '' });

  useEffect(() => {
    fetchVendors();
  }, [filter]);

  const fetchVendors = async () => {
    try {
      const params = new URLSearchParams();
      if (filter.criticality) params.append('criticality', filter.criticality);
      if (filter.risk_rating) params.append('risk_rating', filter.risk_rating);

      const response = await axios.get(`${API_URL}/api/vendors?${params}`);
      setVendors(response.data.vendors);
    } catch (error) {
      console.error('Failed to fetch vendors:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center py-12">Loading...</div>;
  }

  const criticalCount = vendors.filter(v => v.criticality === 'Critical').length;
  const highRiskCount = vendors.filter(v => v.risk_rating === 'High').length;
  const totalValue = vendors.reduce((sum, v) => sum + v.contract_value_annual, 0);

  return (
    <div className="px-4 sm:px-0">
      <div className="mb-8">
        <h2 className="text-3xl font-bold text-gray-900">Vendor Management</h2>
        <p className="mt-2 text-gray-600">
          Vendor management and third-party risk oversight
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
        <StatCard title="Total Vendors" value={vendors.length} />
        <StatCard title="Critical Services" value={criticalCount} />
        <StatCard title="High Risk" value={highRiskCount} />
        <StatCard title="Annual Spend" value={`$${(totalValue / 1000000).toFixed(1)}M`} />
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-4 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Criticality</label>
            <select
              value={filter.criticality}
              onChange={(e) => setFilter({ ...filter, criticality: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2"
            >
              <option value="">All Levels</option>
              <option value="Critical">Critical</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Risk Rating</label>
            <select
              value={filter.risk_rating}
              onChange={(e) => setFilter({ ...filter, risk_rating: e.target.value })}
              className="w-full border border-gray-300 rounded px-3 py-2"
            >
              <option value="">All Ratings</option>
              <option value="High">High</option>
              <option value="Medium">Medium</option>
              <option value="Low">Low</option>
            </select>
          </div>
          <div className="flex items-end">
            <button
              onClick={() => setFilter({ criticality: '', risk_rating: '' })}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
            >
              Clear Filters
            </button>
          </div>
        </div>
      </div>

      {/* Vendors Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Vendor ID
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Name
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Service
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Criticality
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Risk Rating
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                Next Assessment
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                SLA %
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {vendors.map((vendor) => (
              <tr key={vendor.vendor_id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-blue-700">
                  {vendor.vendor_id}
                </td>
                <td className="px-6 py-4 text-sm font-medium text-gray-900">{vendor.name}</td>
                <td className="px-6 py-4 text-sm text-gray-500">{vendor.service}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-medium rounded ${
                    vendor.criticality === 'Critical' ? 'bg-red-100 text-red-800' :
                    vendor.criticality === 'High' ? 'bg-orange-100 text-orange-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {vendor.criticality}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`px-2 py-1 text-xs font-medium rounded ${
                    vendor.risk_rating === 'High' ? 'bg-red-100 text-red-800' :
                    vendor.risk_rating === 'Medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {vendor.risk_rating}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                  {vendor.next_assessment}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {vendor.sla_compliance}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-sm text-gray-500">
        Showing {vendors.length} vendor{vendors.length !== 1 ? 's' : ''}
      </div>
    </div>
  );
}

function StatCard({ title, value }: any) {
  return (
    <div className="bg-white shadow rounded-lg p-4">
      <div className="text-sm font-medium text-gray-500 mb-1">{title}</div>
      <div className="text-3xl font-bold text-blue-700">{value}</div>
    </div>
  );
}
